import React, { useCallback, useContext, useMemo, useState } from 'react';
import { RevShareResult, RevShareEntry } from '../../models/RevShareData';
import { AdminTierTypeEnum, tierOrder } from '../../services/drillDown';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { Spacer } from '../../components';
import { AvailablePricebooks, PricebookBundle } from '../../models/PricebookData';
import { PaidByEnum } from '../../models/LicenseData';
import {
  getEntriesForPricebook,
  getPricebookInheritanceStatus,
} from '../../data/rev-share/helpers/revShareSettingsUtils';
import { sortAlphabetically } from '../../helpers/sortUtils';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { useLoading } from '../../hooks/useLoading';
import { useMutation } from '@aesop-fables/scrinium';
import { isAxiosError } from 'axios';
import {
  DeleteRevenueShareEntry,
  OverrideRevShare,
  RevertToInherited,
} from '../../data/rev-share/mutations';
import AccentBar from './components/AccentBar';
import PricebookRevShareCard from './components/PricebookRevShareCard';
import RemoveEntryModal from './components/RemoveEntryModal';
import RevertToInheritedModal from './components/RevertToInheritedModal';
import RevShareEntryDrawer from './RevShareEntryDrawer';
import RevShareCardSkeleton from './components/RevShareCardSkeleton';

interface RevShareCardProps {
  loading: boolean;
  timedOut: boolean;
  adminTier: AdminTierTypeEnum;
  availablePricebooks: PricebookBundle[];
  parentAvailablePricebooks: AvailablePricebooks | undefined;
  revShareSettings: RevShareResult | undefined;
}

const RevShareCard: React.FC<RevShareCardProps> = ({
  loading,
  timedOut,
  adminTier,
  availablePricebooks,
  parentAvailablePricebooks,
  revShareSettings,
}) => {
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const deleteEntry = useMutation(new DeleteRevenueShareEntry());
  const revertToInherited = useMutation(new RevertToInherited());
  const overrideRevShare = useMutation(new OverrideRevShare());

  const [entryToRemove, setEntryToRemove] = useState<RevShareEntry | null>(null);
  const [pricebookToRevert, setPricebookToRevert] = useState<PricebookBundle | null>(null);
  const [selectedPricebook, setSelectedPricebook] = useState<PricebookBundle | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<RevShareEntry | null>(null);

  const revShareDrawerOpen = selectedPricebook !== null;

  const sortedPricebooks = useMemo(() => {
    return [...availablePricebooks].sort((a, b) =>
      sortAlphabetically(a.pricebook.pricebookName, b.pricebook.pricebookName),
    );
  }, [availablePricebooks]);

  const memberPaysPricebooks = useMemo(
    () => sortedPricebooks.filter(pb => pb.pricebook.paidBy === PaidByEnum.User),
    [sortedPricebooks],
  );

  const orgPaysPricebooks = useMemo(
    () => sortedPricebooks.filter(pb => pb.pricebook.paidBy === PaidByEnum.Org),
    [sortedPricebooks],
  );

  const handleAddRevShare = useCallback((pricebook: PricebookBundle) => {
    setSelectedPricebook(pricebook);
    setSelectedEntry(null);
  }, []);

  const handleViewEdit = useCallback(
    (entry: RevShareEntry) => {
      const pricebook = availablePricebooks.find(pb => pb.pricebook.id === entry.pricebookId);

      setSelectedPricebook(pricebook ?? null);
      setSelectedEntry(entry);
    },
    [availablePricebooks],
  );

  const handleCloseDrawer = useCallback(() => {
    setSelectedPricebook(null);
    setSelectedEntry(null);
  }, []);

  const handleRemoveClick = useCallback((entry: RevShareEntry) => {
    setEntryToRemove(entry);
  }, []);

  const parentTierName = useMemo(() => {
    const tierIndex = tierOrder.indexOf(adminTier);
    return tierIndex > 0 ? tierOrder[tierIndex - 1] : '';
  }, [adminTier]);

  const parentPricebookIds = useMemo(
    () =>
      new Set(
        (parentAvailablePricebooks?.pricebookBundleDtos ?? []).map(pb => pb.pricebook.id),
      ),
    [parentAvailablePricebooks],
  );

  const handleInherit = useCallback((pricebook: PricebookBundle) => {
    setPricebookToRevert(pricebook);
  }, []);

  const handleCloseRevertModal = useCallback(() => {
    setPricebookToRevert(null);
  }, []);

  const handleRevertConfirm = useCallback(
    async (pricebook: PricebookBundle) => {
      try {
        setLoading(true);
        await revertToInherited.action({ pricebookId: pricebook.pricebook.id, adminTier });
        setToast(
          new Toast({
            severity: 'success',
            open: true,
            message: 'Reverted to inherited fee share.',
            autoHideDuration: 6000,
          }),
        );
      } catch (err) {
        console.error('Failed to revert to inherited fee share:', err);
        const errorMessage =
          (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) ||
          'Failed to revert to inherited fee share.';
        setToast(
          new Toast({
            severity: 'error',
            open: true,
            message: errorMessage,
            autoHideDuration: 6000,
          }),
        );
      } finally {
        setLoading(false);
        setPricebookToRevert(null);
      }
    },
    [adminTier, revertToInherited, setLoading, setToast],
  );

  const handleOverride = useCallback(
    async (pricebook: PricebookBundle) => {
      try {
        setLoading(true);
        await overrideRevShare.action({ pricebookId: pricebook.pricebook.id, adminTier });
        setToast(
          new Toast({
            severity: 'success',
            open: true,
            message: 'Copied inherited fee share entries. You can now edit any entry.',
            autoHideDuration: 6000,
          }),
        );
      } catch (err) {
        console.error('Failed to override fee share:', err);
        const errorMessage =
          (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) ||
          'Failed to override fee share.';
        setToast(
          new Toast({
            severity: 'error',
            open: true,
            message: errorMessage,
            autoHideDuration: 6000,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [adminTier, overrideRevShare, setLoading, setToast],
  );

  const handleCloseRemoveModal = useCallback(() => {
    setEntryToRemove(null);
  }, []);

  const handleRemoveConfirm = useCallback(
    async (entry: RevShareEntry) => {
      try {
        setLoading(true);
        await deleteEntry.action({
          entryId: entry.id,
          adminTier,
        });
        setToast(
          new Toast({
            severity: 'success',
            open: true,
            message: 'Record removed!',
            autoHideDuration: 6000,
          }),
        );
      } catch (err) {
        console.error('Failed to remove fee share entry:', err);
        const errorMessage =
          (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) ||
          'Failed to remove fee share entry.';
        setToast(
          new Toast({
            severity: 'error',
            open: true,
            message: errorMessage,
            autoHideDuration: 6000,
          }),
        );
      } finally {
        setLoading(false);
        setEntryToRemove(null);
      }
    },
    [deleteEntry, adminTier, setLoading, setToast],
  );

  if (loading) {
    return <RevShareCardSkeleton />;
  }

  if (timedOut) {
    return (
      <Card variant='ghost' color='primary'>
        <CardContent sx={{ textAlign: 'center' }} className='centered-content'>
          <Spacer height='xxs' />
          <Typography variant='p16SemiBold' color='secondary.main'>
            Error loading fee share data
          </Typography>
          <Spacer height='xxs' />
        </CardContent>
      </Card>
    );
  }

  if (!availablePricebooks.length) {
    return (
      <Card variant='ghost' color='primary'>
        <CardContent sx={{ textAlign: 'center' }} className='centered-content'>
          <Spacer height='xxs' />
          <Typography variant='p16SemiBold' color='secondary.main'>
            No pricebooks available
          </Typography>
          <Spacer height='xxs' />
        </CardContent>
      </Card>
    );
  }

  const renderPricebookSection = (
    title: string,
    accentColor: string,
    pricebooks: PricebookBundle[],
  ) => {
    if (!pricebooks.length) return null;

    return (
      <Box display='flex' flexDirection='column' rowGap='15px'>
        <Box display='flex' alignItems='center' columnGap='10px' mb='5px'>
          <AccentBar color={accentColor} />
          <Typography variant='p16SemiBold' color='secondary.main'>
            {title}
          </Typography>
        </Box>
        {pricebooks.map(pb => (
          <PricebookRevShareCard
            key={pb.pricebook.id}
            pricebook={pb}
            revShareEntries={getEntriesForPricebook(revShareSettings, pb.pricebook.id, adminTier)}
            inheritanceStatus={getPricebookInheritanceStatus(
              revShareSettings,
              pb.pricebook.id,
              adminTier,
            )}
            parentTierName={parentTierName}
            existsOnParentTier={parentPricebookIds.has(pb.pricebook.id)}
            onAddRevShare={handleAddRevShare}
            onViewEdit={handleViewEdit}
            onRemoveRevShareEntry={handleRemoveClick}
            onInherit={handleInherit}
            onOverride={handleOverride}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Grid container>
        <Grid size={{ md: 12, lg: 10, xl: 8 }}>
          <Box display='flex' flexDirection='column' rowGap={5}>
            {renderPricebookSection('Member pays', 'primary.main', memberPaysPricebooks)}
            {renderPricebookSection('Organization pays', 'alternate.main', orgPaysPricebooks)}
          </Box>
        </Grid>
      </Grid>

      <RemoveEntryModal
        onClose={handleCloseRemoveModal}
        entry={entryToRemove}
        onConfirm={handleRemoveConfirm}
      />

      <RevertToInheritedModal
        onClose={handleCloseRevertModal}
        pricebook={pricebookToRevert}
        parentTierName={parentTierName}
        onConfirm={handleRevertConfirm}
      />

      <RevShareEntryDrawer
        open={revShareDrawerOpen}
        onClose={handleCloseDrawer}
        pricebook={selectedPricebook}
        existingEntry={selectedEntry}
        revShareSettings={revShareSettings}
        adminTier={adminTier}
      />
    </Box>
  );
};

export default RevShareCard;
